import { type Product } from "@/lib/product-utils";
import { searchSimilarProducts } from "@/lib/shop-agent-client";
import { productFromListItem } from "@/lib/shopper-search-items";

const SIMILAR_PRODUCTS_CACHE_LIMIT = 50;
const PDP_RAIL_SIZE = 6;
// The route's bound on the products a rail may name.
const MAX_RAIL_SOURCE_UUIDS = 100;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SIMILAR_PRODUCTS_CACHE_TTL_MS = 10 * 60 * 1000;

type SimilarProductsCacheEntry = {
  products: Product[];
  cachedAtMs: number;
};

const similarProductsCache = new Map<string, SimilarProductsCacheEntry>();

export function addToSimilarProductsCache(key: string, products: Product[]): void {
  if (similarProductsCache.size >= SIMILAR_PRODUCTS_CACHE_LIMIT) {
    const oldestKey = similarProductsCache.keys().next().value;
    if (oldestKey) similarProductsCache.delete(oldestKey);
  }
  similarProductsCache.set(key, {
    products,
    cachedAtMs: Date.now(),
  });
}

export function getFromSimilarProductsCache(key: string): Product[] | null {
  const entry = similarProductsCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.cachedAtMs > SIMILAR_PRODUCTS_CACHE_TTL_MS) {
    similarProductsCache.delete(key);
    return null;
  }
  return entry.products;
}

/**
 * A similar-products rail from shop-agent's `similar_products` route: one
 * ranked /v1 more-like-this page of list items that never includes a source
 * product; its cards link by `/v1`'s `uuid`.
 *
 * `products` are the rail's source products (the PDP product, or a
 * collection's products in display order); their `uuid`s are sent, so a
 * product without a UUID-shaped `uuid` cannot be a source. With no source
 * left, the rail is empty.
 */
export async function fetchSimilarProductsRail({
  products,
  limit,
}: {
  products: Product[];
  limit: number;
}): Promise<Product[]> {
  const uuids = Array.from(
    new Set(
      products
        .map((p) => (p as Partial<{ uuid: unknown }>).uuid)
        .filter((uuid): uuid is string => typeof uuid === "string" && UUID_REGEX.test(uuid))
        .map((uuid) => uuid.toLowerCase())
    )
  ).slice(0, MAX_RAIL_SOURCE_UUIDS);
  if (uuids.length === 0) return [];
  const response = await searchSimilarProducts({ uuids, limit });
  return response.products.map(productFromListItem);
}

export async function fetchSimilarProducts(product: Product): Promise<Product[]> {
  try {
    // Ranked by /v1 and never the current product.
    const products = await fetchSimilarProductsRail({ products: [product], limit: PDP_RAIL_SIZE });
    return products.slice(0, PDP_RAIL_SIZE);
  } catch (error) {
    console.error("Error fetching similar products:", error);
    return [];
  }
}
